import React from "react";
import { connect } from "react-redux";
import { Col, Form, Row } from "react-bootstrap";
import PropTypes from "prop-types";

import ConditionalButton from "../../../layout/conditional-button";

import { get as getSetting, save as saveSetting } from "../../../actions/settings";
import { loading, send as pageInfo } from "../../../actions/page";
import { pack as sendAlert } from "../../../actions/alert";

const EVENTS = [
  {
    key: "stripe.payment.received",
    caption: "Stripe Payment Received",
    description: "Raised when a tracked Stripe payment succeeds.",
  },
  {
    key: "survey.divorce.received",
    caption: "Divorce Survey Received",
    description: "Raised when the divorce survey hook posts back into Matters.",
  },
  {
    key: "survey.namechange.received",
    caption: "Name Change Survey Received",
    description: "Raised when the name change survey hook posts back into Matters.",
  },
  {
    key: "lead.task.assigned",
    caption: "Lead Task Assigned",
    description: "Raised when a lead task or follow-up is assigned.",
    defaults: {
      chat: false,
      notification: true,
      connectedOnly: true,
    },
  },
  {
    key: "board.task.assigned",
    caption: "Board Task Assigned",
    description: "Raised when a board task is assigned.",
    defaults: {
      chat: false,
      notification: true,
      connectedOnly: true,
    },
  },
];

const DEFAULT_EVENT_SETTINGS = EVENTS.reduce((acc, event) => ({
  ...acc,
  [event.key]: {
    chat: true,
    notification: true,
    connectedOnly: true,
    ...(event.defaults || {}),
  },
}), {});

const clone = (value) => JSON.parse(JSON.stringify(value || {}));

const safeParseJson = (value, fallback = {}) => {
  try {
    return typeof value === "string" ? JSON.parse(value) : (value || fallback);
  } catch (_err) {
    return fallback;
  }
};

const mergeEvents = (value) => {
  const merged = clone(DEFAULT_EVENT_SETTINGS);
  EVENTS.forEach((event) => {
    merged[event.key] = {
      ...merged[event.key],
      ...(value?.[event.key] || {}),
    };
  });
  return merged;
};

class EventSettings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      setting: null,
      events: clone(DEFAULT_EVENT_SETTINGS),
    };
  }

  componentDidMount = async () => {
    await this.props.pageInfo({ title: "Event Settings", breadcrumb: "Events" });
    await this.loadData();
  };

  loadData = async () => {
    this.props.loading(true);
    try {
      const setting = await this.props.getSetting({ authentic: true, name: "EVENTS" });
      this.setState({
        setting,
        events: mergeEvents(safeParseJson(setting?.value, DEFAULT_EVENT_SETTINGS)),
      });
    } finally {
      this.props.loading(false);
    }
  };

  onToggle = (eventKey, field) => (e) => {
    const checked = e.target.checked;
    this.setState((prev) => ({
      events: {
        ...prev.events,
        [eventKey]: {
          ...(prev.events?.[eventKey] || {}),
          [field]: checked,
        },
      },
    }));
  };

  onSaveClick = async () => {
    const events = mergeEvents(this.state.events);

    this.props.loading(true);
    try {
      const result = await this.props.saveSetting({
        uuid: this.state.setting?.uuid,
        name: "EVENTS",
        category: "Services",
        description: "System event chat and dashboard notification delivery settings",
        visibility: this.state.setting?.visibility ?? 3,
        priority: this.state.setting?.priority ?? 0,
        value: JSON.stringify(events),
      });

      this.setState({ setting: result, events });
      this.props.sendAlert(["Event settings saved."], "success");
    } finally {
      this.props.loading(false);
    }
  };

  renderEvent = (event) => {
    const value = this.state.events?.[event.key] || {};

    return (
      <Col xs="12" key={event.key}>
        <div className="border-bottom pb-3 mb-3">
          <Row className="align-items-center g-3">
            <Col xs="12" lg="5">
              <div className="fw-bold">{event.caption}</div>
              <div className="text-muted small">{event.description}</div>
            </Col>

            <Col xs="12" sm="4" lg="2">
              <Form.Check
                type="switch"
                id={`${event.key}.chat`}
                label="Chat"
                checked={Boolean(value.chat)}
                onChange={this.onToggle(event.key, "chat")}
              />
            </Col>

            <Col xs="12" sm="4" lg="2">
              <Form.Check
                type="switch"
                id={`${event.key}.notification`}
                label="Notification"
                checked={Boolean(value.notification)}
                onChange={this.onToggle(event.key, "notification")}
              />
            </Col>

            <Col xs="12" sm="4" lg="3">
              <Form.Check
                type="switch"
                id={`${event.key}.connectedOnly`}
                label="Only when connected"
                checked={Boolean(value.connectedOnly)}
                onChange={this.onToggle(event.key, "connectedOnly")}
              />
            </Col>
          </Row>
        </div>
      </Col>
    );
  };

  render() {
    return (
      <>
        <Col md="8" className="float-right text-end">
          <ConditionalButton
            enabledVariant="outline-primary"
            tooltip="Save Event Settings"
            display
            enabled
            onEnabledClick={this.onSaveClick}
            icon="save"
          >
            Save
          </ConditionalButton>
        </Col>

        <Col xs="12">
          <Row>
            <Col xs="12">
              <h2 className="small-title">System Events</h2>
            </Col>
            {EVENTS.map(this.renderEvent)}
          </Row>
        </Col>
      </>
    );
  }
}

EventSettings.propTypes = {
  loading: PropTypes.func.isRequired,
  pageInfo: PropTypes.func.isRequired,
  getSetting: PropTypes.func.isRequired,
  saveSetting: PropTypes.func.isRequired,
  sendAlert: PropTypes.func.isRequired,
};

export default connect(null, {
  loading,
  pageInfo,
  getSetting,
  saveSetting,
  sendAlert,
})(EventSettings);
